import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Sparkles, X, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const TOOLTIP_GAP = 16;       // px space between spotlight edge and tooltip
const SPOTLIGHT_PAD = 8;      // px padding around target element
const TOOLTIP_W = 360;        // tooltip width — keep in sync with className
const SCROLL_OFFSET = 100;    // px buffer above target when scrolling into view

/**
 * Pick which testid to use for the current step based on the active mode
 * (Single Slide vs Multi-slide Deck). Some steps (title, slides) have
 * different anchor IDs per mode.
 *
 * For steps that declare `anchor_testids_cycle` (multi-anchor cycling
 * spotlight — e.g., mode + approach), the caller passes a `cycleIndex`
 * so the spotlight rotates between the two anchors.
 */
function resolveAnchorTestId(step, mode, cycleIndex = 0) {
  if (step.anchor_testids_cycle && step.anchor_testids_cycle.length > 0) {
    const arr = step.anchor_testids_cycle;
    return arr[cycleIndex % arr.length];
  }
  if (mode === "deck" && step.anchor_testid_deck) return step.anchor_testid_deck;
  return step.anchor_testid;
}

/**
 * Detect the active mode by looking for the `pressed` state on the mode
 * toggle buttons. The PPT generator owns mode state — we read it via DOM.
 */
function detectMode() {
  const multi = document.querySelector("[data-testid='mode-multi']");
  if (multi && multi.getAttribute("aria-pressed") === "true") return "deck";
  // Fall back to the more reliable visual signal: which one has the
  // active background. Single is the default in the UI.
  return "single";
}

/**
 * Coachmark tour — spotlights the actual UI elements one at a time with
 * a tooltip. Auto-advances when the per-step completion predicate fires
 * (typed content, clicked anchor, picked option, etc.).
 *
 * Mounted at the page level (PPT Studio). Fetches its own status; renders
 * nothing if `needs_onboarding=false`. Dismissed permanently via Skip OR
 * by completing the last step (Generate click).
 */
export default function CoachmarkTour({ active }) {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [sampleTitle, setSampleTitle] = useState("");
  const [sampleContent, setSampleContent] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);     // spotlight rect from getBoundingClientRect
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, placement: "bottom" });
  // Cycling spotlight: for steps with `anchor_testids_cycle` we rotate
  // the spotlight between the listed anchors so the user perceives both
  // options as one connected choice (Single ↔ Multi, Design ↔ Research).
  const [cycleIndex, setCycleIndex] = useState(0);
  const dismissingRef = useRef(false);

  // Reset cycle index whenever the step changes so a fresh cycling step
  // always starts on its first anchor (rather than wherever the prior
  // cycling step happened to land).
  useEffect(() => {
    setCycleIndex(0);
  }, [stepIndex]);

  // Drive the cycle clock — toggles every 1.4s for any step that opts
  // into multi-anchor highlighting. Idle for single-anchor steps.
  useEffect(() => {
    if (!open) return;
    const s = steps[stepIndex];
    if (!s || !s.anchor_testids_cycle || s.anchor_testids_cycle.length < 2) return;
    const id = setInterval(() => {
      setCycleIndex(i => (i + 1) % s.anchor_testids_cycle.length);
    }, 1400);
    return () => clearInterval(id);
  }, [open, stepIndex, steps]);

  // ── Fetch tour status when the page mounts ────────────────────────
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/me/onboarding-status`, { withCredentials: true });
        if (cancelled) return;
        if (res.data?.needs_onboarding && (res.data.steps || []).length > 0) {
          setSteps(res.data.steps);
          setSampleTitle(res.data.sample_title || "");
          setSampleContent(res.data.sample_content || "");
          setStepIndex(0);
          setOpen(true);
        }
      } catch {
        // Silent — never block the page on a tour fetch failure.
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  const step = steps[stepIndex];
  // True for the welcome / centered intro step — has neither a single
  // anchor nor a cycling pair.
  const isNoAnchorStep = !!step && !step.anchor_testid && !(step.anchor_testids_cycle && step.anchor_testids_cycle.length);

  // ── Measure the current anchor + reposition tooltip on every change ──
  // Re-runs on step change, window resize, scroll. We DON'T use
  // ResizeObserver because the anchors are fixed-position inputs/buttons
  // that don't resize themselves — only the viewport changes.
  const measure = useCallback(() => {
    if (!open || !step) return;
    // Welcome / intro steps have no DOM anchor — render the tooltip
    // centered with no spotlight.
    if (isNoAnchorStep) {
      setRect({ centered: true });
      setTooltipPos({
        top: Math.max(120, window.innerHeight / 2 - 140),
        left: Math.max(16, window.innerWidth / 2 - TOOLTIP_W / 2),
        placement: "center",
      });
      return;
    }
    const mode = detectMode();
    const testid = resolveAnchorTestId(step, mode, cycleIndex);
    const el = document.querySelector(`[data-testid='${testid}']`);
    if (!el) {
      // Anchor not in DOM (e.g. user is on a different page). Hide the
      // spotlight + tooltip until it re-appears.
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - SPOTLIGHT_PAD,
      left: r.left - SPOTLIGHT_PAD,
      width: r.width + SPOTLIGHT_PAD * 2,
      height: r.height + SPOTLIGHT_PAD * 2,
    });
    // For cycling steps the tooltip is anchored to the BOUNDING BOX of
    // BOTH anchors so it doesn't visually jump every 1.4s — compute the
    // union with the sibling anchor when present.
    let tipR = r;
    if (step.anchor_testids_cycle && step.anchor_testids_cycle.length > 1) {
      const otherTestid = step.anchor_testids_cycle.find(t => t !== testid);
      const otherEl = otherTestid && document.querySelector(`[data-testid='${otherTestid}']`);
      if (otherEl) {
        const o = otherEl.getBoundingClientRect();
        tipR = {
          top: Math.min(r.top, o.top),
          bottom: Math.max(r.bottom, o.bottom),
          left: Math.min(r.left, o.left),
          right: Math.max(r.right, o.right),
          width: Math.max(r.right, o.right) - Math.min(r.left, o.left),
          height: Math.max(r.bottom, o.bottom) - Math.min(r.top, o.top),
        };
      }
    }
    // Place tooltip below the spotlight by default; flip to above if it
    // would overflow the viewport (bottom 100px reserved for footer/CTA).
    const wouldOverflow = tipR.bottom + TOOLTIP_GAP + 180 > window.innerHeight;
    const placement = wouldOverflow ? "top" : "bottom";
    const top = placement === "bottom"
      ? tipR.bottom + TOOLTIP_GAP
      : tipR.top - TOOLTIP_GAP - 180;
    // Clamp left so the tooltip doesn't overflow horizontally.
    const naturalLeft = tipR.left + (tipR.width / 2) - (TOOLTIP_W / 2);
    const left = Math.max(16, Math.min(naturalLeft, window.innerWidth - TOOLTIP_W - 16));
    setTooltipPos({ top, left, placement });
  }, [open, step, cycleIndex, isNoAnchorStep]);

  useEffect(() => {
    if (!open) return;
    // Scroll the target into a comfortable viewport position before
    // measuring. The `block: "center"` keeps it away from sticky headers.
    // Welcome (no-anchor) steps skip scrolling — they render centered.
    if (step && !isNoAnchorStep) {
      const mode = detectMode();
      const testid = resolveAnchorTestId(step, mode, 0);
      const el = document.querySelector(`[data-testid='${testid}']`);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.top < SCROLL_OFFSET || r.bottom > window.innerHeight - 200) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
    // Measure once initially + on every resize/scroll.
    const t = setTimeout(measure, 350);  // wait for smooth scroll to settle
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    // Poll briefly to catch async re-renders (e.g., approach-picker
    // expanding the page height after a click).
    const poll = setInterval(measure, 500);
    return () => {
      clearTimeout(t);
      clearInterval(poll);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, stepIndex, measure, step, isNoAnchorStep]);

  // ── Auto-advance based on `complete_when` predicate ──────────────────
  // Polls the anchor element to see if its completion condition is met.
  // Cheap (one DOM lookup every 600ms) but does the job without invasive
  // event listeners on every input on the page.
  useEffect(() => {
    if (!open || !step) return;
    if (step.complete_when === "manual") return;
    if (isNoAnchorStep) return;
    // Cycling steps are always manual — no per-anchor predicate.
    if (step.anchor_testids_cycle) return;
    const mode = detectMode();
    const testid = resolveAnchorTestId(step, mode, 0);
    const check = () => {
      const el = document.querySelector(`[data-testid='${testid}']`);
      if (!el) return false;
      switch (step.complete_when) {
        case "filled": {
          // textarea or input — check value length vs min_chars threshold.
          const v = (el.value ?? el.textContent ?? "").trim();
          return v.length >= (step.min_chars ?? 1);
        }
        case "click": {
          // Track aria-pressed for toggle buttons (mode, approach picker).
          // The Generate step uses `complete_when: manual` because the
          // button is disabled until the user fills required fields —
          // we can't disambiguate "user hasn't filled fields yet" from
          // "user just clicked Generate".
          return el.getAttribute("aria-pressed") === "true";
        }
        case "selected": {
          // Shadcn Select sets aria-expanded; we check the textual value.
          const txt = (el.textContent || "").trim();
          return txt.length > 0 && txt !== "Select...";
        }
        default:
          return false;
      }
    };
    const poll = setInterval(() => {
      if (check()) {
        clearInterval(poll);
        // For the last step (Generate), mark complete instead of advancing.
        if (stepIndex === steps.length - 1) {
          dismiss(true);
        } else {
          setStepIndex(i => Math.min(steps.length - 1, i + 1));
        }
      }
    }, 600);
    return () => clearInterval(poll);
  }, [open, stepIndex, step, steps.length, isNoAnchorStep]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dismiss (Skip or auto-on-last-step) ──────────────────────────────
  const dismiss = async (silent = false) => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setOpen(false);
    try {
      await axios.post(`${API}/me/onboarding-complete`, {}, { withCredentials: true });
    } catch {
      // ignore — UI is already dismissed
    } finally {
      dismissingRef.current = false;
    }
    // Notify the rest of the app so the celebration modal knows tour
    // ended and the user is about to generate their first deck.
    if (!silent) {
      window.dispatchEvent(new CustomEvent("onboarding:skipped"));
    }
  };

  // ── "Use sample content" shortcut on the content step ────────────────
  const fillSampleContent = () => {
    // Fill via the React-controlled input by simulating native input event.
    // Shadcn/native inputs both react to a programmatic input event.
    const titleEl = document.querySelector("[data-testid='title-input']") ||
                    document.querySelector("[data-testid='topic-input']");
    const contentEl = document.querySelector("[data-testid='content-text-input']");
    const setNative = (el, value) => {
      if (!el) return;
      const desc = Object.getOwnPropertyDescriptor(
        el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
        "value",
      );
      desc.set.call(el, value);
      el.dispatchEvent(new Event("input", { bubbles: true }));
    };
    if (titleEl && !titleEl.value) setNative(titleEl, sampleTitle);
    if (contentEl) setNative(contentEl, sampleContent);
  };

  // ── Advance helper: honours per-step `default_on_advance`. Used by
  // the approach step so a user who Next's through it without clicking
  // either card still ends up with "design" (Use only my content) picked
  // — otherwise the Generate button stays disabled at the last step.
  const advanceStep = () => {
    if (!step) return;
    const def = step.default_on_advance;
    if (def && def.click_testid) {
      const target = document.querySelector(`[data-testid='${def.click_testid}']`);
      // Only synthesize the click if no sibling in the cycle group is
      // already pressed (the user might have already picked an option).
      const cycle = step.anchor_testids_cycle || [];
      const anyPressed = cycle.some(tid => {
        const el = document.querySelector(`[data-testid='${tid}']`);
        return el && el.getAttribute("aria-pressed") === "true";
      });
      if (target && !anyPressed) {
        target.click();
      }
    }
    setStepIndex(i => Math.min(steps.length - 1, i + 1));
  };

  if (!open || !step || !rect) {
    // Render nothing when measuring fails — better than flashing a tooltip
    // over the wrong element. The interval re-measures and shows the
    // moment the anchor becomes visible.
    return null;
  }

  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;
  const isCentered = !!rect.centered;
  // `isWelcome` controls the larger tooltip styling — bound to the
  // dedicated welcome step (anchor_testid AND no cycle group). Cycling
  // steps (mode, approach) get the standard compact tooltip.
  const isWelcome = step.id === "welcome";
  // Generate step gets an extra-prominent CTA color so the user is
  // strongly nudged to click Generate (low drop-off).
  const isGenerateStep = step.id === "generate";

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────
           For anchored steps: 4 quadrant rects around the spotlight so
           the user can only interact with the highlighted element.
           For the welcome (centered) step: a single full-screen dimmer. */}
      {isCentered ? (
        <div
          className="fixed inset-0 z-[150] bg-black/60 pointer-events-auto"
          onClick={() => dismiss(false)}
          data-testid="coachmark-overlay"
        />
      ) : (
        <div className="fixed inset-0 z-[150] pointer-events-none" data-testid="coachmark-overlay">
          {/* Top band */}
          <div className="absolute bg-black/60 pointer-events-auto"
               style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top) }}
               onClick={() => dismiss(false)} />
          {/* Bottom band */}
          <div className="absolute bg-black/60 pointer-events-auto"
               style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }}
               onClick={() => dismiss(false)} />
          {/* Left band */}
          <div className="absolute bg-black/60 pointer-events-auto"
               style={{ top: rect.top, left: 0, width: Math.max(0, rect.left), height: rect.height }}
               onClick={() => dismiss(false)} />
          {/* Right band */}
          <div className="absolute bg-black/60 pointer-events-auto"
               style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }}
               onClick={() => dismiss(false)} />
          {/* Spotlight border ring — purely decorative, no pointer interception */}
          <div
            className="absolute rounded-lg ring-4 ring-teal-400 ring-offset-2 ring-offset-transparent shadow-2xl animate-pulse-slow"
            style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
            data-testid="coachmark-spotlight"
          />
        </div>
      )}

      {/* ── Tooltip card ────────────────────────────────────────────── */}
      <div
        className={`fixed z-[160] bg-white rounded-xl shadow-2xl border border-gray-100 animate-in fade-in slide-in-from-bottom-2 duration-200 ${
          isWelcome ? "p-7" : "p-5"
        }`}
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          width: isWelcome ? 440 : TOOLTIP_W,
        }}
        data-testid="coachmark-tooltip"
      >
        <button
          onClick={() => dismiss(false)}
          className="absolute top-2.5 right-2.5 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Skip tour"
          data-testid="coachmark-skip"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Sparkles className={`w-4 h-4 ${isGenerateStep ? "text-amber-500" : "text-teal-500"}`} />
          <span className={`text-[10px] font-bold uppercase tracking-wider ${
            isGenerateStep ? "text-amber-600" : "text-teal-600"
          }`} data-testid="coachmark-step-counter">
            {isWelcome ? "Get started" : `Step ${stepIndex + 1} of ${steps.length}`}
          </span>
        </div>

        <h3
          className={`font-bold text-gray-900 mb-1.5 ${isWelcome ? "text-xl" : "text-base"}`}
          data-testid="coachmark-title"
        >
          {step.title}
        </h3>
        <p
          className={`text-gray-600 leading-relaxed mb-4 ${isWelcome ? "text-sm" : "text-sm"}`}
          data-testid="coachmark-body"
        >
          {step.body}
        </p>

        {/* Optional "Use sample content" link on the content step */}
        {step.show_sample_link && sampleContent && (
          <button
            onClick={fillSampleContent}
            className="block w-full text-left text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md px-3 py-2 mb-3 transition-colors"
            data-testid="coachmark-use-sample"
          >
            ✨ Use sample content (auto-fills title + content)
          </button>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === stepIndex ? "w-5 bg-teal-500" : "w-1 bg-gray-300"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {!isWelcome && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStepIndex(i => Math.max(0, i - 1))}
                disabled={isFirst}
                className="h-7 px-2 text-xs text-gray-600 disabled:opacity-30"
                data-testid="coachmark-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back
              </Button>
            )}
            {isLast ? (
              <Button
                size="sm"
                onClick={() => dismiss(true)}
                className="h-8 px-3 text-xs bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-md"
                data-testid="coachmark-finish"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                Got it, generate now!
              </Button>
            ) : isWelcome ? (
              <Button
                size="sm"
                onClick={advanceStep}
                className="h-8 px-4 text-xs bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                data-testid="coachmark-next"
              >
                Let's go
                <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={advanceStep}
                className="h-7 px-2.5 text-xs bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="coachmark-next"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
